import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import InlineValidationMessage, {
  InlineValidationType
} from '@ids/inline-validation-message';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import { styleOverrideReason } from '@ids/core';
import Radio from './Radio';
import { Fieldset, Legend } from './StyledRadio';

const prefix = 'idsRadioGroup';

const {
  arrayOf,
  bool,
  func,
  node,
  oneOfType,
  shape,
  string,
  oneOf
} = PropTypes;

const RadioOptionType = shape({
  label: string,
  value: string,
  disabled: bool,
  onChange: func
});

/** Find the radio that is checked */
const getCheckedValue = children => {
  let value = null;
  let matched = false;
  React.Children.forEach(children, radio => {
    if (radio && radio.props && radio.props.checked) {
      ({ value } = radio.props);
      matched = true;
    }
  });
  return matched ? { value } : undefined;
};

/** Normalize options */
const getOptions = options =>
  options.map(option => {
    // option should be {label: string, value: string}
    // if string normalize to an object
    if (typeof option === 'string' || typeof option === 'number') {
      return {
        label: option,
        value: option
      };
    }

    return option;
  });

/** A collection of choices */
export default class RadioGroup extends React.PureComponent {
  static contextType = ThemeContext;

  static propTypes = {
    /**
     * A list of <Radio>
     */
    children: node,
    /**
     * The optional CSS class name that is added to the root node.
     */
    defaultValue: string,
    /**
     * Disable all radio buttons.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Label for the Radio group. Can be a React node.
     */
    label: oneOfType([string, node]),
    /**
     * The name that will be applied to all child Radio buttons.
     */
    name: string,
    /**
     * Callback function that is fired when a Radio button has been selected.
     */
    onChange: func,
    /**
     * Set the children Radio buttons by using an array of values or an array of objects.
     */
    options: arrayOf(oneOfType([RadioOptionType, string])),
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason,
    /**
     * The application's ThemeProvider determines the default theme the component will use. Use
     * theme prop to override (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax').
     */
    theme: oneOf(themes),
    /**
     * The value used to currently select the Radio button value.
     */
    value: string,
    /**
     * Determines the type of Validation Message : ['error', 'warning']
     */
    validationType: InlineValidationType,
    /**
     * Validation Message
     */
    validationMessage: string,
    /**
     * Layout type vertical or horizontal.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    vertical: bool
  };

  static defaultProps = {
    children: null,
    defaultValue: undefined,
    disabled: false,
    label: undefined,
    name: undefined,
    onChange() {},
    options: [],
    styleOverrideReason: undefined,
    theme: undefined,
    value: undefined,
    validationMessage: '',
    validationType: '',
    vertical: false
  };

  constructor(props) {
    super(props);
    let value;
    if (typeof props.value !== 'undefined') {
      ({ value } = props);
      // eslint-disable-next-line no-negated-condition
    } else if (typeof props.defaultValue !== 'undefined') {
      value = props.defaultValue;
    } else {
      const checkedValue = getCheckedValue(props.children);
      value = checkedValue && checkedValue.value;
    }

    this.state = {
      value
    };
    this.radioGroupName = uniqueId(prefix);
    this.ariaId = uniqueId('idsRadio-');
  }

  static getDerivedStateFromProps(props) {
    if (typeof props.value !== 'undefined') {
      return { value: props.value };
    }

    const checkedValue = getCheckedValue(props.children);
    if (checkedValue) {
      return { value: checkedValue.value };
    }

    return null;
  }

  handleRadioChange = event => {
    const lastValue = this.state.value;
    const { value } = event.target;

    if (typeof this.props.value === 'undefined') {
      this.setState({ value });
    }

    // firing the group onChange event
    if (this.props.onChange && value !== lastValue) {
      this.props.onChange(event);
    }
  };

  render() {
    const {
      disabled,
      label,
      name,
      options,
      theme,
      validationMessage,
      validationType,
      vertical,
      onChange,
      ...restProps
    } = this.props;

    const { currentTheme } = getTheme({ theme }, this.context);
    // not a common use case, using options prop and not <Radio> children
    let children;
    if (options && options.length > 0) {
      children = getOptions(options).map((option, index) => (
        <Radio
          key={option.value || index}
          checked={
            // eslint-disable-next-line no-negated-condition
            this.state.value !== undefined
              ? this.state.value === option.value
              : false
          }
          disabled={'disabled' in option ? option.disabled : disabled}
          value={option.value}
          name={name || this.radioGroupName}
          // radio group theme and validationType wins here vs radio
          theme={currentTheme}
          validationType={option.validationType}
          aria-describedby={this.ariaId}
          aria-labelledby={this.ariaId}
          onChange={event => {
            // firing the child onChange event
            if (typeof option.onChange === 'function') {
              option.onChange(event);
            }

            this.handleRadioChange(event);
          }}
        >
          {option.label}
        </Radio>
      ));
    } else {
      // using <Radio>
      children = React.Children.map(this.props.children, child =>
        React.cloneElement(child, {
          // reach out to the child.props to compare group value
          checked: this.state.value === child.props.value,
          disabled: child.props.disabled || disabled,
          name: name || this.radioGroupName,
          // radio group theme and validationType wins here vs radio
          theme: currentTheme,
          validationType,
          'aria-describedby': this.ariaId,
          onChange: event => {
            // firing the child onChange event
            if (typeof child.props.onChange === 'function') {
              child.props.onChange(event);
            }

            this.handleRadioChange(event);
          }
        })
      );
    }

    return (
      <Fieldset {...restProps} vertical={vertical}>
        {label && <Legend id={this.ariaId}>{label}</Legend>}
        {children}
        <InlineValidationMessage
          id={this.ariaId}
          message={validationMessage}
          type={validationType}
        />
      </Fieldset>
    );
  }
}
